import { useEffect, useMemo, useRef } from "react";
import { UseFormWatch, UseFormSetValue } from "react-hook-form";
import { FieldDependencyRule, FieldDependencyCondition } from "models/schemas";

/**
 * Evaluates a single condition against a source field value.
 */
export const evaluateCondition = (
  sourceValue: string | undefined,
  condition: FieldDependencyCondition,
): boolean => {
  if (sourceValue == null) return false;

  switch (condition.operator) {
    case "=":
      return sourceValue === condition.value;
    case ":":
      return sourceValue
        .toLowerCase()
        .includes((condition.value as string).toLowerCase());
    case "in":
      return (
        Array.isArray(condition.value) && condition.value.includes(sourceValue)
      );
    case "!=":
      return sourceValue !== condition.value;
    case "!:":
      return !sourceValue
        .toLowerCase()
        .includes((condition.value as string).toLowerCase());
    case "^":
      return sourceValue
        .toLowerCase()
        .startsWith((condition.value as string).toLowerCase());
    case "!^":
      return !sourceValue
        .toLowerCase()
        .startsWith((condition.value as string).toLowerCase());
    case "not_in":
      return (
        Array.isArray(condition.value) && !condition.value.includes(sourceValue)
      );
    default:
      return false;
  }
};

/**
 * Evaluates all conditions for a rule and returns the result.
 * First matching condition wins. Falls back to defaultResult.
 */
export const evaluateRule = (
  rule: FieldDependencyRule,
  getFieldValue: (fieldName: string) => string | undefined,
): string | null => {
  for (const condition of rule.conditions) {
    const sourceValue = getFieldValue(condition.sourceField);
    if (evaluateCondition(sourceValue, condition)) {
      return condition.result;
    }
  }
  return rule.defaultResult;
};

/**
 * Hook that watches source fields and auto-sets target fields based on dependency rules.
 *
 * @param rulesMap - Map of targetField to rule from team config
 * @param watch - react-hook-form watch function
 * @param setValue - react-hook-form setValue function
 */
export const useFieldDependencyRules = (
  rulesMap: Record<string, FieldDependencyRule> | undefined,
  watch: UseFormWatch<any>,
  setValue: UseFormSetValue<any>,
): void => {
  const previousValuesRef = useRef<Record<string, string | null>>({});

  // Collect all unique source fields from rules
  const sourceFields = useMemo(
    () =>
      rulesMap
        ? Array.from(
            new Set(
              Object.values(rulesMap).flatMap((r) =>
                r.conditions.map((c) => c.sourceField),
              ),
            ),
          )
        : [],
    [rulesMap],
  );

  // Watch all source fields
  const watchedValues = watch(sourceFields);

  useEffect(() => {
    if (!rulesMap || Object.keys(rulesMap).length === 0) return;

    const getFieldValue = (fieldName: string): string | undefined => {
      const index = sourceFields.indexOf(fieldName);
      if (index === -1) return undefined;
      return watchedValues[index] as string | undefined;
    };

    for (const [targetField, rule] of Object.entries(rulesMap)) {
      const result = evaluateRule(rule, getFieldValue);
      const previousResult = previousValuesRef.current[targetField];

      // Only update if the result changed to avoid infinite loops
      if (result !== previousResult) {
        previousValuesRef.current[targetField] = result;
        if (result !== null) {
          setValue(targetField, result, { shouldValidate: true });
        }
      }
    }
  }, [rulesMap, watchedValues, setValue, sourceFields]);
};
